import React from 'react';
import { Circle, G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Voltage drop — long wire run, source at one end, load at other, gradient + voltmeters. */
export function DiagramVoltageDrop() {
  const sx = 40, lx = 310;
  const topY = 100, botY = 160;

  return (
    <DiagramCanvas>
      {/* Source box */}
      <Rect x={sx - 18} y={topY - 18} width={20} height={botY - topY + 36} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.6" />
      <SvgText x={sx - 8} y={(topY + botY) / 2 + 3} fill={DT.varColor} fontSize="9"
        fontFamily={DT.mono} textAnchor="middle" fontWeight="700">SRC</SvgText>

      {/* Load box */}
      <Rect x={lx - 2} y={topY - 18} width={20} height={botY - topY + 36} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.6" />
      <SvgText x={lx + 8} y={(topY + botY) / 2 + 3} fill={DT.varColor} fontSize="9"
        fontFamily={DT.mono} textAnchor="middle" fontWeight="700">LOAD</SvgText>

      {/* Conductors with thickness — gradient strips for "drop" */}
      <Line x1={sx} y1={topY} x2={lx} y2={topY} stroke={DT.stroke} strokeWidth="4" />
      <Line x1={sx} y1={botY} x2={lx} y2={botY} stroke={DT.stroke} strokeWidth="4" />

      {/* Heat-loss "puffs" along the run */}
      {[100, 160, 220, 280].map((px, i) => (
        <G key={i}>
          <Polygon points={`${px},${topY - 8} ${px - 4},${topY - 14} ${px + 4},${topY - 14}`} fill={DT.varColor} opacity={0.5} />
          <SvgText x={px} y={topY - 18} fill={DT.varColor} fontSize="7" fontFamily={DT.mono} textAnchor="middle" opacity={0.7}>I²R</SvgText>
        </G>
      ))}

      {/* Current direction arrows */}
      {[120, 200, 270].map((px, i) => (
        <Polygon key={i} points={`${px + 6},${topY} ${px},${topY - 4} ${px},${topY + 4}`} fill={DT.arrow} />
      ))}
      {[120, 200, 270].map((px, i) => (
        <Polygon key={i} points={`${px - 6},${botY} ${px},${botY - 4} ${px},${botY + 4}`} fill={DT.arrow} />
      ))}

      {/* Source voltmeter */}
      <Circle cx={sx + 0} cy={topY - 36} r={11} fill={DT.fill} stroke={DT.leader} strokeWidth="1.4" />
      <SvgText x={sx} y={topY - 33} fill={DT.leader} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">V_S</SvgText>
      {/* Load voltmeter */}
      <Circle cx={lx + 8} cy={topY - 36} r={11} fill={DT.fill} stroke={DT.leader} strokeWidth="1.4" />
      <SvgText x={lx + 8} y={topY - 33} fill={DT.leader} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">V_L</SvgText>
      <Line x1={sx} y1={topY - 25} x2={sx} y2={topY} stroke={DT.leader} strokeWidth="0.8" strokeDasharray="2,2" />
      <Line x1={lx + 8} y1={topY - 25} x2={lx + 8} y2={topY} stroke={DT.leader} strokeWidth="0.8" strokeDasharray="2,2" />

      {/* L dimension */}
      <Line x1={sx} y1={botY + 30} x2={lx} y2={botY + 30} stroke={DT.dim} strokeWidth="1" />
      <Line x1={sx} y1={botY + 26} x2={sx} y2={botY + 34} stroke={DT.dim} strokeWidth="1" />
      <Line x1={lx} y1={botY + 26} x2={lx} y2={botY + 34} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={(sx + lx) / 2} y={botY + 42} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="middle">L (one-way ft)</SvgText>

      {/* Conductor size badge */}
      <Rect x={140} y={(topY + botY) / 2 - 8} width={70} height={16} fill="rgba(0,0,0,0.45)" stroke={DT.varColor} strokeWidth="0.8" />
      <SvgText x={175} y={(topY + botY) / 2 + 4} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">AWG / CM</SvgText>

      {/* Labels */}
      <LeaderLine x1={sx} y1={topY - 24} x2={20} y2={50} varName="V_S" desc="source" anchor="start" />
      <LeaderLine x1={lx + 8} y1={topY - 24} x2={310} y2={50} varName="V_L" desc="load" anchor="end" />
      <LeaderLine x1={175} y1={topY + 4} x2={170} y2={210} varName="VD" desc="V_S − V_L" anchor="middle" />

      <FormulaTag formula="VD = 2 · K · I · L / CM   (1φ; ×√3 for 3φ)" />
    </DiagramCanvas>
  );
}
